import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useLeadStats } from "@/hooks/use-lead-stats";
import { summarizeByUser, weekStart } from "@/lib/lead-stats-calc";
import { CONTACT_ACTIVITY_TYPES, NOT_A_CONTACT_STATUSES, countContacts } from "@/lib/oppdag-kpi-calc";
import { osloDateString } from "@/lib/oslo-time";

/** Midnatt mandag (norsk tid) som instant; uka starter der. */
function weekStartInstant(now: Date): string {
  const monday = weekStart(osloDateString(now));
  // Ett døgn ekstra bakover dekker tidssoneforskjellen; countContacts filtrerer på norsk dato.
  return new Date(Date.parse(`${monday}T00:00:00Z`) - 86_400_000).toISOString();
}

/**
 * KPI-ene øverst i Oppdag (VIB-2986): kontakter i dag og denne uken, og møter
 * booket denne uken, for den aktive brukeren. En brukers uke er få rader, så
 * ingen paginering.
 */
export function useOppdagKpis() {
  const { activeUser } = useImpersonation();
  const uid = activeUser?.id ?? null;
  const now = new Date();
  const today = osloDateString(now);
  const week = { fromDate: weekStart(today), toDate: today };
  const { data: leadRows } = useLeadStats(week, { now, enabled: !!uid });

  const query = useQuery({
    queryKey: ["oppdag-kpis", uid, week.fromDate],
    queryFn: async () => {
      const since = weekStartInstant(now);
      const [activities, meetings] = await Promise.all([
        supabase
          .from("customer_activities")
          .select("occurred_at")
          .eq("user_id", uid!)
          .in("type", CONTACT_ACTIVITY_TYPES as string[])
          .not("status", "in", `(${NOT_A_CONTACT_STATUSES.join(",")})`)
          .gte("occurred_at", since),
        supabase
          .from("crm_meetings")
          .select("created_at")
          .eq("booked_by", uid!)
          .gte("created_at", since),
      ]);
      if (activities.error) throw activities.error;
      if (meetings.error) throw meetings.error;
      return { activities: activities.data ?? [], meetings: meetings.data ?? [] };
    },
    enabled: !!uid,
    staleTime: 60 * 1000,
  });

  const lead = summarizeByUser((leadRows ?? []).filter((r) => r.user_id === uid), now, week)[0];
  const contacts = countContacts(query.data?.activities ?? [], { today: lead?.today.calls ?? 0, week: lead?.week.calls ?? 0 }, now);
  const meetingsThisWeek = (query.data?.meetings ?? []).filter((m) => osloDateString(m.created_at) >= week.fromDate).length;
  return { contacts, meetingsThisWeek, isLoading: query.isLoading };
}
